import { StarFilledIcon, StarIcon } from '@radix-ui/react-icons';
import { useState } from 'react';
import { Form } from 'react-router';
import { AuthenticityTokenInput } from 'remix-utils/csrf/react';

import { Button } from '~/components/ui/button';
import { cn } from '~/styles/extended-theme.ts';
import { type PropsWithClassName } from '~/utils/props-with-classname';

interface Props {
  gymSlug: string;
  rating: number | null;
  returnRoute: string;
}

export function RatingForm({ gymSlug, rating, returnRoute, className }: PropsWithClassName<Props>) {
  const [hoverValue, setHoverValue] = useState<number | null>(null);

  const highlight = hoverValue ?? rating ?? 0;

  const isHighlighted = (value: number) => value <= highlight;

  const handleHover = (value: number | null) => {
    setHoverValue(value);
  };

  return (
    <Form action={`/gym/${gymSlug}/set-rating`} method="post" preventScrollReset={true}>
      <AuthenticityTokenInput />
      <input type="hidden" name="returnRoute" value={returnRoute} />

      <div className={cn('mb-2 flex', className)}>
        <RatingButton value={1} highlighted={isHighlighted(1)} onHover={handleHover} />
        <RatingButton value={2} highlighted={isHighlighted(2)} onHover={handleHover} />
        <RatingButton value={3} highlighted={isHighlighted(3)} onHover={handleHover} />
        <RatingButton value={4} highlighted={isHighlighted(4)} onHover={handleHover} />
        <RatingButton value={5} highlighted={isHighlighted(5)} onHover={handleHover} />
      </div>

      {rating !== null ? (
        <Button type="submit" variant="ghost-secondary" name="value">
          Delete
        </Button>
      ) : null}
    </Form>
  );
}

export function RatingButton({
  value,
  highlighted,
  onHover,
}: {
  value: number;
  highlighted: boolean;
  onHover: (value: number | null) => void;
}) {
  return (
    <Button
      type="submit"
      value={value.toString(10)}
      name="value"
      variant="ghost"
      size="icon-lg"
      onMouseEnter={() => onHover(value)}
      onMouseLeave={() => onHover(null)}
      onFocus={() => onHover(value)}
      onBlur={() => onHover(null)}
      className="hover:bg-unset h-9 w-10 rounded-none pr-1"
    >
      {highlighted ? (
        <StarFilledIcon className="size-full text-accent" />
      ) : (
        <StarIcon className="size-full text-primary" />
      )}
    </Button>
  );
}
